import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Mail, Menu, Search, X } from "lucide-react";
import { NAV, SITE } from "../data/site";
import { LotusRule, TempleGateLogo } from "./Ornaments";

export function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M3.5 15.5 12 19.5l8.5-4" />
      <path d="M3.5 11.5 12 15.5l8.5-4" />
      <path d="M3.5 7.5 12 3.5l8.5 4-8.5 4z" />
    </svg>
  );
}

export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  // On the homepage the header floats transparently over the hero scroll until the page scrolls.
  const overlay = location.pathname === "/" && atTop && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className={`site-header${overlay ? " is-overlay" : ""}`}>
      <div className="header-inner container">
        <Link
          to="/"
          className="brand brand--heritage"
          aria-label={`${SITE.name} – ${SITE.tagline}, home`}
        >
          <TempleGateLogo className="brand-gate" />
          <span className="brand-text">
            <span className="brand-name">{SITE.name}</span>
            <LotusRule className="brand-rule" />
          </span>
        </Link>

        <nav
          ref={navRef}
          className={`main-nav${mobileOpen ? " is-open" : ""}`}
          aria-label="Main"
        >
          <ul>
            {NAV.map(group =>
              group.children ? (
                <li
                  key={group.label}
                  className="has-menu"
                  onMouseEnter={() => setOpenMenu(group.label)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    type="button"
                    className={`nav-link${group.children.some(c => location.pathname === c.href) ? " active" : ""}`}
                    aria-expanded={openMenu === group.label}
                    onClick={() =>
                      setOpenMenu(openMenu === group.label ? null : group.label)
                    }
                  >
                    {group.label}
                    <ChevronDown aria-hidden="true" />
                  </button>
                  <ul
                    className={`dropdown${openMenu === group.label ? " is-open" : ""}`}
                  >
                    {group.children.map(item => (
                      <li key={item.href}>
                        <Link to={item.href}>
                          <span className="dropdown-title">
                            {item.label}
                            {item.badge && (
                              <span className="dropdown-badge">
                                {item.badge}
                              </span>
                            )}
                          </span>
                          {item.description && (
                            <span className="dropdown-desc">
                              {item.description}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={group.label}>
                  <NavLink to={group.href!} className="nav-link">
                    {group.label}
                  </NavLink>
                </li>
              )
            )}
            <li className="nav-cta">
              <Link to="/contact" className="btn btn-primary">
                <Mail aria-hidden="true" />
                Contact Us
              </Link>
            </li>
          </ul>
        </nav>

        <div className="header-actions">
          <Link to="/search" className="icon-btn" aria-label="Search">
            <Search aria-hidden="true" />
          </Link>
          <Link to="/contact" className="btn btn-primary btn-contact">
            <Mail aria-hidden="true" />
            Contact
          </Link>
          <button
            type="button"
            className="icon-btn menu-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(v => !v)}
          >
            {mobileOpen ? (
              <X aria-hidden="true" />
            ) : (
              <Menu aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
